import { Button, FormField, Icon } from '../components/ui';
import { useUI } from '../context/UIContext';
import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import TokenQuotaSummary from '../components/TokenQuotaSummary';

export default function SettingsPage() {
  const { confirm } = useUI();
  const { theme, setTheme } = useTheme();
  const { user, login } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadRevision, setLoadRevision] = useState(0);

  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    setLoadError(null);
    api.getUser(user.id)
      .then(data => {
        setProfile(data);
        setBotToken(data.telegram_bot_token || '');
        setChatId(data.telegram_chat_id || '');
      })
      .catch(() => setLoadError('Не удалось загрузить настройки. Попробуйте ещё раз.'))
      .finally(() => setLoading(false));
  }, [user?.id, loadRevision]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!botToken.trim() || !chatId.trim()) return;
    setSaving(true);
    try {
      const updated = await api.updateUserTelegram(user.id, botToken.trim(), chatId.trim());
      setProfile(updated);
      login({ ...user, telegram_bot_token: updated.telegram_bot_token, telegram_chat_id: updated.telegram_chat_id });
      showMsg('success', 'Telegram-бот подключён');
    } catch {
      showMsg('error', 'Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!await confirm('Отключить Telegram-бот?')) return;
    setClearing(true);
    try {
      const updated = await api.clearUserTelegram(user.id);
      setProfile(updated);
      setBotToken('');
      setChatId('');
      login({ ...user, telegram_bot_token: null, telegram_chat_id: null });
      showMsg('success', 'Telegram-бот отключён');
    } catch {
      showMsg('error', 'Не удалось отключить бот');
    } finally {
      setClearing(false);
    }
  };

  if (loading) return <div className="loading">Загрузка...</div>;

  const connected = profile?.telegram_bot_token && profile?.telegram_chat_id;

  return (
    <div className="settings-page" style={{ maxWidth: 600 }}>
      <h1 className="page-title" style={{ marginBottom: 24 }}>Настройки</h1>
      <TokenQuotaSummary />

      <section className="card" aria-labelledby="appearance-title">
        <h2 id="appearance-title" className="card-title">Оформление</h2>
        <p className="text-muted mt-2">Выберите удобную тему. Настройка сохраняется для вашего аккаунта в этом браузере.</p>
        <div className="settings-theme-controls" role="group" aria-label="Тема интерфейса">
          {[['light', 'Светлая', 'sun'], ['dark', 'Тёмная', 'moon']].map(([value, label, icon]) =>
            <Button key={value} type="button" className={`btn ${theme === value ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={theme === value} onClick={() => setTheme(value)}><Icon name={icon} />{label}</Button>)}
        </div>
      </section>
      {loadError && <div className="alert alert-error" role="alert">{loadError}<Button className="btn" onClick={() => setLoadRevision(value => value + 1)}>Повторить</Button></div>}

      <div className="card" style={{ marginBottom: 24 }}>
        <h2 className="card-title" style={{ marginBottom: 4 }}>Профиль</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>{profile?.email}</p>
        <p style={{ color: 'var(--text-muted)' }}>{profile?.display_name}</p>
        {profile?.is_admin && (
          <span className="badge badge-running" style={{ marginTop: 8 }}>Администратор</span>
        )}
      </div>

      <div className="card">
        <h2 className="card-title" style={{ marginBottom: 8 }}>Telegram-уведомления</h2>
        <p className="text-muted" style={{ marginBottom: 16, fontSize: '0.9rem' }}>
          Подключите личного Telegram-бота, чтобы получать уведомления о запросах к оператору.
        </p>

        {connected && (
          <div style={{
            background: 'var(--success-bg)',
            border: '1px solid var(--success)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            color: 'var(--success)',
            fontSize: '0.9rem',
          }}>
            Бот подключён. Webhook: <code style={{ fontSize: '0.85rem' }}>/api/telegram/webhook/{user.id}</code>
          </div>
        )}

        {message && (
          <div style={{
            padding: '10px 14px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: '0.9rem',
            background: message.type === 'success' ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
            border: `1px solid ${message.type === 'success' ? 'var(--success)' : 'var(--danger)'}`,
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave}>
          <FormField label={<>Bot Token</>}>
          <input className="form-input" type="password" value={botToken} onChange={e => setBotToken(e.target.value)} placeholder="123456789:ABCdef..." required />
          </FormField>
          <FormField label={<>Chat ID</>}>
          <input className="form-input" value={chatId} onChange={e => setChatId(e.target.value)} placeholder="-100123456789" required />
          </FormField>
          <div className="flex gap-2">
            <Button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            {connected && (
              <Button
                className="btn"
                type="button"
                style={{ color: 'var(--danger)', border: '1px solid currentColor' }}
                disabled={clearing}
                onClick={handleClear}
              >
                {clearing ? '...' : 'Отключить'}
              </Button>
            )}
          </div>
        </form>

        <div style={{ marginTop: 24, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          <p style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Как настроить бота:</p>
          <ol style={{ paddingLeft: 18, lineHeight: 1.8 }}>
            <li>Откройте <strong>@BotFather</strong> в Telegram → /newbot → получите <em>Bot Token</em></li>
            <li>Напишите боту любое сообщение, затем откройте<br/>
              <code>https://api.telegram.org/bot{'<TOKEN>'}/getUpdates</code><br/>
              и найдите <code>chat.id</code> — это и есть <em>Chat ID</em>
            </li>
            <li>После сохранения зарегистрируйте webhook:<br/>
              <code>https://api.telegram.org/bot{'<TOKEN>'}/setWebhook?url=https://ВАШ_ДОМЕН/api/telegram/webhook/{user?.id}</code>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
